import { View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { useTranslation } from 'react-i18next';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import { usePartners } from 'shared/library/partners';
import { useLocale } from 'shared/library/i18n/use-locale';
import { getTranslationList } from 'shared/library/i18n/get-translation-list';
import { insuranceDocumentUrls } from 'shared/library/insurance-documents';

interface LitterInsuranceScreenProps extends Props {}

const LitterInsuranceScreen = ({}: LitterInsuranceScreenProps) => {
  const { countryCode } = useLocale();
  const { insurancePartner } = usePartners();
  const { t, i18n } = useTranslation('screens');
  const coverageList = getTranslationList(i18n, 'screens', 'litter_insurance.coverage_list');
  const hiddenDefectsCoverageList = getTranslationList(
    i18n,
    'screens',
    'litter_insurance.hidden_defects_coverage_list',
  );
  const insuranceDocuments = [
    {
      href: insuranceDocumentUrls.litterInsuranceTerms,
      label: t('litter_insurance.documents.terms'),
    },
    {
      href: insuranceDocumentUrls.litterInsurancePreAndPostPurchaseInformation,
      label: t('litter_insurance.documents.pre_and_post_purchase_information'),
    },
    {
      href: insuranceDocumentUrls.litterInsuranceProductFacts,
      label: t('litter_insurance.documents.product_facts'),
    },
    {
      href: insuranceDocumentUrls.customerInformation,
      label: t('litter_insurance.documents.customer_information'),
    },
    {
      href: insuranceDocumentUrls.cancellationRight,
      label: t('litter_insurance.documents.cancellation_right'),
    },
  ] as const;

  return (
    <>
      <View className={'p-3 md:p-6 md:py-12'}>
        <View className={`mx-auto w-full max-w-screen-xl`}>
          <View className={'gap-6 md:flex-row-reverse lg:gap-20'}>
            <View className={'md:w-2/5'}>
              <View
                className={'border-brand-brown-dark/15 rounded border bg-white p-3 md:rounded-xl'}
              >
                <Image
                  src={insurancePartner.image}
                  alt={insurancePartner.name}
                  containerClassName={'aspect-3/1 mx-auto w-full max-w-80'}
                  imageClassName={'object-contain'}
                  priority={true}
                />
              </View>

              {countryCode !== 'SE' && (
                <Text className={'text-brand-brown-dark mt-3 text-center text-sm italic'}>
                  Currently only available in Sweden.
                </Text>
              )}
            </View>

            <View className={'flex-1'}>
              <H1
                title={t('litter_insurance.title')}
                subtitle={t('litter_insurance.description', { partner: insurancePartner.name })}
                marginTop={false}
                marginBottom={false}
              />

              <H2
                title={t('litter_insurance.what_is_litter_insurance_title')}
                subtitle={t('litter_insurance.what_is_litter_insurance_description')}
                marginBottom={false}
              />

              <H2
                title={t('litter_insurance.coverage_title')}
                subtitle={t('litter_insurance.coverage_description')}
                marginBottom={false}
              />

              <View className={'mt-3 gap-3'}>
                {coverageList.map((item) => (
                  <View key={item} className={'flex-row gap-3'}>
                    <Text className={'text-brand-brown-dark md:text-lg'}>{'\u2022'}</Text>
                    <Text className={'text-brand-brown-dark flex-1 md:text-lg'}>{item}</Text>
                  </View>
                ))}
              </View>

              <H2
                title={t('litter_insurance.hidden_defects_title')}
                subtitle={t('litter_insurance.hidden_defects_description')}
                marginBottom={false}
              />

              <View className={'mt-3 gap-3'}>
                {hiddenDefectsCoverageList.map((item) => (
                  <View key={item} className={'flex-row gap-3'}>
                    <Text className={'text-brand-brown-dark md:text-lg'}>{'\u2022'}</Text>
                    <Text className={'text-brand-brown-dark flex-1 md:text-lg'}>{item}</Text>
                  </View>
                ))}
              </View>

              <H2
                title={t('litter_insurance.why_it_matters_title')}
                subtitle={t('litter_insurance.why_it_matters_description')}
                marginBottom={false}
              />

              <H2
                title={t('litter_insurance.duration_title')}
                subtitle={t('litter_insurance.duration_description')}
                marginBottom={false}
              />

              <H2
                title={t('litter_insurance.available_to_all_title')}
                subtitle={t('litter_insurance.available_to_all_description')}
                marginBottom={false}
              />

              <H2
                title={t('litter_insurance.documents_title')}
                subtitle={t('litter_insurance.documents_description')}
                marginBottom={false}
              />

              <View className={'mt-3 flex-row flex-wrap gap-3'}>
                {insuranceDocuments.map((document) => (
                  <Link
                    key={document.href}
                    href={document.href}
                    textClassName={'text-brand-brown-dark underline'}
                    openInNewTab={true}
                  >
                    {document.label}
                  </Link>
                ))}
              </View>
            </View>
          </View>
        </View>
      </View>
    </>
  );
};

export { LitterInsuranceScreen };
export type { LitterInsuranceScreenProps };
